// Week view rules:
// - Must fit entirely in viewport (no vertical scrolling)
// - Editable and drag/reorder enabled
// - Shows completion overlay
// - No metrics, no coaching text, no analytics

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isToday,
  startOfMonth,
  parseISO,
  isPast,
} from 'date-fns';
import { cn } from '@/lib/utils';
import { Loader2, GripVertical, Maximize2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import { CalendarWorkoutStack } from '../cards/CalendarWorkoutStack';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DayView } from './DayView';
import { DroppableDayCell } from '../DroppableDayCell';
import { PulseTimeline } from './PulseTimeline';
import { SwipeIndicator } from '../SwipeIndicator';
import { Card } from '@/components/ui/layout/card';
import { SessionCard } from '@/components/sessions/SessionCard';
import { RestDayCard } from '@/components/sessions/RestDayCard';
import { toast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

import type {
  CalendarItem,
  GroupedCalendarItem,
} from '@/types/calendar';
import {
  groupDuplicateSessions,
} from '@/types/calendar';
import { fetchCalendarMonth, normalizeCalendarMonth, buildMergedCalendarItemsForDay } from '@/lib/calendar-month';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import {
  useUpdatePlannedSession,
} from '@/hooks/useCalendarMutations';
import { markDragOperationComplete } from '@/hooks/useAutoMatchSessions';
import type { PlannedWorkout, CompletedActivity } from '@/types';
import type { CalendarSession } from '@/lib/api';
import { normalizeSportType, mapIntensityToIntent } from '@/lib/session-utils';
import { capitalizeTitle } from '@/adapters/calendarAdapter';
import { isHighlightedIntent } from '@/types/calendar';

interface WeekCalendarProps {
  currentDate: Date;
  onActivityClick?: (planned: PlannedWorkout | null, completed: CompletedActivity | null, session?: CalendarSession | null) => void;
  /** When set, open the session popup for this id once week data is loaded */
  sessionIdToOpen?: string | null;
  /** Called after session popup has been opened (clears sessionIdToOpen) */
  onSessionOpened?: () => void;
}

/**
 * DraggableSessionWrapper - Wraps a session card for drag-and-drop
 *
 * Interaction model:
 * - Drag anywhere on the card to move it
 * - Click the expand button to open details
 */
function DraggableSessionWrapper({
  item,
  session,
  children,
  onClick,
}: {
  item: CalendarItem;
  session: CalendarSession | null;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  // Allow dragging mutable planned items that aren't completed.
  // Prefer explicit planned_session_id; fallback to session.id for legacy payloads.
  const mutableSessionId = session?.planned_session_id || session?.id || null;
  const canDrag = Boolean(mutableSessionId && !session?.completed_activity_id);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !canDrag,
    data: {
      type: 'planned-session',
      session,
      item,
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'relative group transition-all duration-200',
        isDragging && 'opacity-40 scale-95',
        canDrag && 'cursor-grab active:cursor-grabbing'
      )}
      onClick={onClick}
      {...(canDrag ? { ...attributes, ...listeners } : {})}
    >
      {children}

      {canDrag && (
        <TooltipProvider delayDuration={400}>
          <div className="absolute top-1 right-1 flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick?.();
                  }}
                  className="p-0.5 rounded bg-white/[0.08] backdrop-blur-md border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                  aria-label="Open activity details"
                >
                  <Maximize2 className="h-3 w-3 text-muted-foreground" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                Expand for details
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'p-0.5 rounded opacity-0 group-hover:opacity-100',
                    'bg-white/[0.08] backdrop-blur-md border border-white/10',
                    'transition-opacity duration-150 pointer-events-none'
                  )}
                  aria-label="Draggable"
                >
                  <GripVertical className="h-3 w-3 text-muted-foreground" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                Drag to reschedule
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * WeekCalendar Component
 *
 * Expanded week view with vertical stacking, drag-and-drop support,
 * and smooth animations for session moves.
 */
export function WeekCalendar({
  currentDate,
  onActivityClick,
  sessionIdToOpen,
  onSessionOpened,
}: WeekCalendarProps) {
  const queryClient = useQueryClient();
  const updateSession = useUpdatePlannedSession();
  const isMobile = useIsMobile();

  // PointerSensor with distance constraint: click (<8px) vs drag (≥8px)
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedItem, setDraggedItem] = useState<CalendarItem | null>(null);
  const [dragSourceDate, setDragSourceDate] = useState<string | null>(null);
  const [recentlyDropped, setRecentlyDropped] = useState<string | null>(null);

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
  const monthStart = startOfMonth(currentDate);
  const monthKey = format(monthStart, 'yyyy-MM');
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(weekEnd, 'yyyy-MM-dd');

  const { data: monthData, isLoading } = useAuthenticatedQuery({
    queryKey: ['calendar', 'month', monthKey],
    queryFn: () => fetchCalendarMonth(currentDate),
    retry: 1,
  });

  // Convert to CalendarItems grouped by day
  const dayDataMap = useMemo(() => {
    if (!monthData) return new Map<string, CalendarItem[]>();

    const normalizedDays = normalizeCalendarMonth(monthData);
    const map = new Map<string, CalendarItem[]>();

    for (const day of normalizedDays) {
      if (day.date < weekStartStr || day.date > weekEndStr) continue;
      const items = buildMergedCalendarItemsForDay(day, monthData.completed_activities || []);
      map.set(day.date, items);
    }

    return map;
  }, [monthData, weekStartStr, weekEndStr]);

  const activityIdBySessionId = useMemo(() => {
    if (!monthData) return {};
    const map: Record<string, string> = {};
    for (const session of [...monthData.planned_sessions, ...monthData.workouts]) {
      if (session.completed_activity_id) {
        map[session.id] = session.completed_activity_id;
      }
    }
    for (const activity of monthData.completed_activities || []) {
      if (activity.planned_session_id) {
        map[activity.planned_session_id] = activity.id;
      }
    }
    return map;
  }, [monthData]);

  // Build session lookup for drag-and-drop
  const sessionById = useMemo(() => {
    if (!monthData) return new Map<string, CalendarSession>();
    const map = new Map<string, CalendarSession>();
    for (const s of [...monthData.planned_sessions, ...monthData.workouts]) {
      map.set(s.id, s);
    }
    return map;
  }, [monthData]);

  // Open session popup when navigated from Today "Expand" (calendarOpenSessionId)
  useEffect(() => {
    if (!sessionIdToOpen || !monthData || !onActivityClick) return;
    const session = sessionById.get(sessionIdToOpen);
    if (!session) return;
    const activity = monthData.completed_activities?.find(
      (a) => a.id === sessionIdToOpen || a.planned_session_id === sessionIdToOpen
    ) ?? null;
    onActivityClick(
      {
        id: session.id,
        date: session.date || '',
        sport: normalizeSportType(session.type),
        intent: mapIntensityToIntent(session.intensity),
        title: capitalizeTitle(session.title || ''),
        description: session.notes || '',
        duration: session.duration_minutes || 0,
        completed: session.status === 'completed',
      },
      activity,
      session
    );
    onSessionOpened?.();
  }, [sessionIdToOpen, monthData, sessionById, onActivityClick, onSessionOpened]);

  const days = useMemo(() => {
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  }, [weekStart, weekEnd]);

  // Stable callback for PulseTimeline — recalculates only when dayDataMap changes
  const getItemsForDay = useCallback((date: Date): CalendarItem[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return dayDataMap.get(dateStr) || [];
  }, [dayDataMap]);

  const getGroupedItemsForDay = (date: Date): GroupedCalendarItem[] => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const items = dayDataMap.get(dateStr);
    if (!items || items.length === 0) return [];
    return groupDuplicateSessions(items);
  };

  const handleCardClick = (item: CalendarItem) => {
    if (monthData && onActivityClick) {
      const session = [...monthData.planned_sessions, ...monthData.workouts].find(
        (s) => s.id === item.id
      );
      const activity = monthData.completed_activities.find(
        (a) => a.id === item.id || a.planned_session_id === item.id
      );

      onActivityClick(
        session
          ? {
              id: session.id,
              date: session.date || '',
              sport: normalizeSportType(session.type),
              intent: mapIntensityToIntent(session.intensity),
              title: capitalizeTitle(session.title || ''),
              description: session.notes || '',
              duration: session.duration_minutes || 0,
              completed: session.status === 'completed',
            }
          : null,
        activity || null,
        session
      );
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const item = event.active.data.current?.item as CalendarItem | undefined;
    const session = event.active.data.current?.session as CalendarSession | undefined;
    if (!item) return;
    
    setActiveId(event.active.id as string);
    setDraggedItem(item);
    // Track source date for ghost placeholder
    if (session?.date) {
      setDragSourceDate(format(parseISO(session.date), 'yyyy-MM-dd'));
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setDraggedItem(null);
    setDragSourceDate(null);
    
    if (!over) return;
    
    const session = active.data.current?.session as CalendarSession | undefined;
    const targetDate = over.data.current?.date as string | undefined;
    
    if (!session || !targetDate) return;
    
    const oldDate = session.date ? format(parseISO(session.date), 'yyyy-MM-dd') : '';
    if (oldDate === targetDate) return;
    
    // Check if moving a past session
    const sessionDate = session.date ? parseISO(session.date) : null;
    const isPastSession = sessionDate && isPast(sessionDate) && !isToday(sessionDate);
    
    if (isPastSession) {
      toast({
        title: 'Session moved',
        description: 'You moved a past session.',
      });
    }
    
    const mutableSessionId = session.planned_session_id || session.id;
    if (!mutableSessionId) {
      toast({
        title: 'Move failed',
        description: 'Missing session ID for move.',
        variant: 'destructive',
      });
      return;
    }
    
    // Trigger drop animation
    setRecentlyDropped(session.id);
    setTimeout(() => setRecentlyDropped(null), 600);
    
    // Perform the move against planned-session endpoint
    updateSession.mutate(
      { sessionId: mutableSessionId, scheduledDate: targetDate },
      {
        onSuccess: () => {
          markDragOperationComplete();
          queryClient.invalidateQueries({ queryKey: ['calendar'], exact: false });
          toast({
            title: 'Session moved',
            description: `Moved to ${format(parseISO(targetDate), 'EEEE, MMM d, yyyy')}`,
          });
        },
      }
    );
  };

  // Show day view if a day is selected
  if (selectedDay) {
    const dateStr = format(selectedDay, 'yyyy-MM-dd');
    const items = dayDataMap.get(dateStr) || [];

    return (
      <DayView
        date={selectedDay}
        items={items}
        onBack={() => setSelectedDay(null)}
        onItemClick={handleCardClick}
      />
    );
  }

  if (isLoading) {
    return (
      <Card className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mr-2" />
        <span className="text-muted-foreground">Loading week...</span>
      </Card>
    );
  }

  // Mobile layout - Pulse Timeline (cyberpunk vertical data timeline)
  if (isMobile) {
    return (
      <div className="h-full overflow-hidden flex flex-col">
        <SwipeIndicator label="week" className="flex-shrink-0" />
        <PulseTimeline
          days={days}
          getItemsForDay={getItemsForDay}
          onDayClick={setSelectedDay}
          onItemClick={handleCardClick}
          className="flex-1 px-1"
        />
      </div>
    );
  }

  // Desktop layout - 7-column grid with drag-and-drop
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col">
        {/* Week Grid - 7 equal columns, content-driven height */}
        <div className="grid grid-cols-7 gap-1.5">
          {days.map((day, idx) => {
            const groupedItems = getGroupedItemsForDay(day);
            const isCurrentDay = isToday(day);
            const dayItems = dayDataMap.get(format(day, 'yyyy-MM-dd')) || [];
            const dayTotal = dayItems.reduce((sum, i) => sum + i.durationMin, 0);

            return (
              <DroppableDayCell
                key={idx}
                date={day}
                className={cn(
                  'rounded-lg border bg-transparent text-card-foreground shadow-sm',
                  'flex flex-col min-h-0 overflow-visible transition-all duration-150',
                  'hover:shadow-md hover:border-border/80',
                  'focus-within:ring-2 focus-within:ring-primary/40 focus-within:shadow-md',
                  isCurrentDay && 'ring-2 ring-primary/50 bg-primary/[0.02]',
                )}
              >
                {/* Day Header - compact, clickable */}
                <div
                  className={cn(
                    'flex-shrink-0 px-1.5 py-1 border-b border-border cursor-pointer hover:bg-muted/30 transition-colors',
                    isCurrentDay && 'bg-primary/5',
                  )}
                  onClick={() => setSelectedDay(day)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9px] font-medium text-muted-foreground uppercase">
                        {format(day, 'EEE')}
                      </span>
                      <span
                        className={cn(
                          'text-sm font-semibold tabular-nums',
                          isCurrentDay ? 'text-primary' : 'text-foreground',
                        )}
                      >
                        {format(day, 'd')}
                      </span>
                    </div>
                    {dayTotal > 0 && (
                      <span className="text-[9px] text-muted-foreground tabular-nums">{dayTotal}m</span>
                    )}
                  </div>
                </div>

                {/* Workout Cards - content area with drag-and-drop */}
                <div className="p-1 min-h-[60px]">
                  {groupedItems.length === 0 ? (
                    <div className="p-1">
                      <RestDayCard density="compact" onClick={() => setSelectedDay(day)} />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {groupedItems.flatMap((group) => group.items).map((item) => {
                        const session = sessionById.get(item.id) || null;
                        const isDropped = recentlyDropped === item.id;
                        const isDragging = activeId === item.id;
                        const dayStr = format(day, 'yyyy-MM-dd');
                        const isGhostSource = dragSourceDate === dayStr && isDragging;
                        
                        return (
                          <DraggableSessionWrapper
                            key={item.id}
                            item={item}
                            session={session}
                            onClick={() => handleCardClick(item)}
                            
                          >
                            {/* Ghost placeholder - shown at original position during drag */}
                            {isGhostSource && (
                              <div className="absolute inset-0 rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 animate-pulse" />
                            )}
                            <div
                              className={cn(
                                'transition-all duration-300',
                                isDropped && 'animate-scale-in ring-2 ring-primary/50 shadow-lg',
                                isDragging && 'opacity-30'
                              )}
                            >
                            <SessionCard
                              session={item}
                              density="compact"
                              className="h-full"
                              highlighted={isHighlightedIntent(item.intent)}
                            />
                            </div>
                          </DraggableSessionWrapper>
                        );
                      })}
                    </div>
                  )}
                </div>
              </DroppableDayCell>
            );
          })}
        </div>
      </div>

      {/* Drag Overlay - follows cursor during drag */}
      <DragOverlay>
        {activeId && draggedItem && (
          <div className="opacity-90 shadow-xl rounded-lg ring-2 ring-primary animate-scale-in">
            <SessionCard
              session={draggedItem}
              density="compact"
              className="w-[200px]"
              highlighted={isHighlightedIntent(draggedItem.intent)}
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
